import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  PRICING_APPLICABLE_CHANNELS,
  PRICING_PROMOTION_TYPES,
  PRICING_TICKET_TYPES,
  createPricing,
  getPricing,
  listProductsLite,
  updatePricing,
  type CreatePricingDto,
  type PricingApplicableChannel,
  type PricingPromotionType,
  type PricingRule,
  type PricingTicketType,
  type UpdatePricingDto,
} from '../lib/api-client';
import { TICKET_TYPE_LABEL } from '../lib/ticket-type-label';

const PROMOTION_TYPE_LABEL: Record<PricingPromotionType, string> = {
  discount_pct: '打折（按比例）',
  fixed_amount_off: '折抵固定金額',
  bonus: '加贈（不影響單價）',
  package_bundle: '組合包',
};

const CHANNEL_LABEL: Record<PricingApplicableChannel, string> = {
  web: 'Web 官網',
  kiosk: 'Kiosk 購票機',
  partner: 'Partner 分銷',
  floor: 'Floor 現場',
  recovery: 'Recovery 補救',
};

const ntd = new Intl.NumberFormat('zh-TW', { maximumFractionDigits: 2 });
const fmtNtd = (n: number): string => `NT$ ${ntd.format(n)}`;

/** 商品某票種之原價：成人→base_price；其他→ticket_prices[type]（無則 base_price）。回字串供 input。 */
function productPerTypeBase(
  p: { base_price: string; ticket_prices: Record<string, string> | null } | undefined,
  ticketType: string,
): string {
  if (!p) return '';
  if (ticketType !== 'adult' && p.ticket_prices && p.ticket_prices[ticketType] != null) {
    return String(p.ticket_prices[ticketType]);
  }
  return p.base_price;
}

interface FormState {
  product_id: string;
  ticket_type: PricingTicketType;
  promotion_type: PricingPromotionType;
  list_price: string;
  discount_pct: string;
  fixed_amount_off: string;
  bonus_product_id: string;
  bonus_quantity: string;
  effective_from: string;
  effective_to: string;
  is_active: boolean;
  applicable_channels: PricingApplicableChannel[];
}

const EMPTY_FORM: FormState = {
  product_id: '',
  ticket_type: 'adult',
  promotion_type: 'discount_pct',
  list_price: '',
  discount_pct: '',
  fixed_amount_off: '',
  bonus_product_id: '',
  bonus_quantity: '',
  effective_from: '',
  effective_to: '',
  is_active: true,
  applicable_channels: [],
};

/** ISO-8601 (ex: 2026-05-15T10:00:00Z) → datetime-local input value (YYYY-MM-DDTHH:mm) */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local 值 → ISO-8601；空字串回 undefined */
function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function ruleToForm(rule: PricingRule): FormState {
  return {
    product_id: rule.product_id,
    ticket_type: rule.ticket_type,
    promotion_type: rule.promotion_type,
    list_price: rule.list_price,
    discount_pct: rule.discount_pct,
    fixed_amount_off: rule.fixed_amount_off ?? '',
    bonus_product_id: rule.bonus_config?.bonus_product_id ?? '',
    bonus_quantity: rule.bonus_config ? String(rule.bonus_config.bonus_quantity) : '',
    effective_from: isoToLocalInput(rule.effective_from),
    effective_to: isoToLocalInput(rule.effective_to),
    is_active: rule.is_active,
    applicable_channels: rule.applicable_channels ?? [],
  };
}

/** 即時試算：顧客此票種實付單價（discount_pct / fixed_amount_off 才有意義）。 */
interface PricePreview {
  list: number;
  final: number;
  save: number;
  zhe: number; // 台灣折數：實付 / 原價 × 10（8 折 = 付 80%）
}
function computePreview(form: FormState): PricePreview | null {
  const list = Number(form.list_price);
  if (!Number.isFinite(list) || list <= 0) return null;
  let final: number | null = null;
  if (form.promotion_type === 'discount_pct') {
    const pct = Number(form.discount_pct);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return null;
    final = Math.round(list * (1 - pct / 100) * 100) / 100;
  } else if (form.promotion_type === 'fixed_amount_off') {
    const amt = Number(form.fixed_amount_off);
    if (!Number.isFinite(amt) || amt <= 0) return null;
    final = Math.max(0, Math.round((list - amt) * 100) / 100);
  }
  if (final === null) return null;
  return { list, final, save: list - final, zhe: list > 0 ? (final / list) * 10 : 0 };
}

export function PricingEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const productsQuery = useQuery({
    queryKey: ['pricing.products-lookup'],
    queryFn: listProductsLite,
  });

  const pricingQuery = useQuery({
    queryKey: ['pricing.detail', id],
    queryFn: () => getPricing(id ?? ''),
    enabled: isEdit && Boolean(id),
  });

  useEffect(() => {
    if (isEdit && pricingQuery.data) {
      setForm(ruleToForm(pricingQuery.data));
    }
  }, [isEdit, pricingQuery.data]);

  const createMutation = useMutation({
    mutationFn: (dto: CreatePricingDto) => createPricing(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing.list'] });
      navigate('/pricing');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (dto: UpdatePricingDto) => updatePricing(id ?? '', dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing.list'] });
      void queryClient.invalidateQueries({ queryKey: ['pricing.detail', id] });
      navigate('/pricing');
    },
  });

  const submitting = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error ?? updateMutation.error;

  const products = productsQuery.data?.items ?? [];
  const preview = useMemo(() => computePreview(form), [form]);

  // 選定商品（給票種限定 + 牌價自動帶用）
  const selectedProduct = products.find((p) => p.id === form.product_id) ?? null;
  // 票種下拉限定為該商品已定義之可售票種（成人 ∪ ticket_prices 鍵）；無選商品則全列。
  // 編輯模式票種固定（下拉 disabled），只顯示既有票種。
  const sellableTicketTypes: PricingTicketType[] = isEdit
    ? [form.ticket_type]
    : selectedProduct
      ? (['adult', ...Object.keys(selectedProduct.ticket_prices ?? {})] as string[])
          .filter((t): t is PricingTicketType =>
            (PRICING_TICKET_TYPES as readonly string[]).includes(t),
          )
          .sort((a, b) => PRICING_TICKET_TYPES.indexOf(a) - PRICING_TICKET_TYPES.indexOf(b))
      : [...PRICING_TICKET_TYPES];

  // 選商品 → 建立模式自動帶入該商品「該票種原價」為牌價；票種重置為成人（恆可售）
  const onSelectProduct = (productId: string): void => {
    setForm((prev) => {
      const next = { ...prev, product_id: productId };
      if (!isEdit && productId) {
        const p = products.find((x) => x.id === productId);
        next.ticket_type = 'adult';
        next.list_price = productPerTypeBase(p, 'adult');
      }
      return next;
    });
  };

  // 選票種 → 建立模式自動帶入該票種之商品原價為牌價
  const onSelectTicketType = (ticketType: PricingTicketType): void => {
    setForm((prev) => {
      const p = products.find((x) => x.id === prev.product_id);
      return { ...prev, ticket_type: ticketType, list_price: productPerTypeBase(p, ticketType) };
    });
  };

  const toggleChannel = (ch: PricingApplicableChannel): void => {
    setForm((prev) => {
      const has = prev.applicable_channels.includes(ch);
      return {
        ...prev,
        applicable_channels: has
          ? prev.applicable_channels.filter((c) => c !== ch)
          : [...prev.applicable_channels, ch],
      };
    });
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);

    if (!form.product_id) {
      setFormError('請選擇商品');
      return;
    }
    const listPriceNum = Number(form.list_price);
    if (!Number.isFinite(listPriceNum) || listPriceNum < 0) {
      setFormError('牌價必須為非負數');
      return;
    }
    if (form.promotion_type === 'discount_pct') {
      const pct = Number(form.discount_pct);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        setFormError('折扣 % 必須介於 0（不含）~ 100');
        return;
      }
    }
    if (form.promotion_type === 'fixed_amount_off') {
      const amt = Number(form.fixed_amount_off);
      if (!Number.isFinite(amt) || amt <= 0) {
        setFormError('折抵金額必須 > 0');
        return;
      }
    }
    if (form.promotion_type === 'bonus') {
      const qty = Number(form.bonus_quantity);
      if (!form.bonus_product_id) {
        setFormError('加贈須指定加贈商品');
        return;
      }
      if (!Number.isInteger(qty) || qty < 1) {
        setFormError('加贈數量必須為 >= 1 的整數');
        return;
      }
    }
    const fromIso = localInputToIso(form.effective_from);
    const toIso = localInputToIso(form.effective_to);
    if (fromIso && toIso && new Date(toIso).getTime() <= new Date(fromIso).getTime()) {
      setFormError('生效迄日須晚於起日');
      return;
    }

    if (isEdit) {
      const dto: UpdatePricingDto = { list_price: listPriceNum, is_active: form.is_active };
      if (form.promotion_type === 'discount_pct') dto.discount_pct = Number(form.discount_pct);
      if (form.promotion_type === 'fixed_amount_off')
        dto.fixed_amount_off = Number(form.fixed_amount_off);
      if (form.promotion_type === 'bonus') {
        dto.bonus_config = {
          bonus_product_id: form.bonus_product_id,
          bonus_quantity: Number(form.bonus_quantity),
        };
      }
      if (fromIso !== undefined) dto.effective_from = fromIso;
      if (toIso !== undefined) dto.effective_to = toIso;
      if (form.applicable_channels.length > 0) dto.applicable_channels = form.applicable_channels;
      updateMutation.mutate(dto);
    } else {
      const dto: CreatePricingDto = {
        product_id: form.product_id,
        ticket_type: form.ticket_type,
        promotion_type: form.promotion_type,
        list_price: listPriceNum,
        is_active: form.is_active,
      };
      if (form.promotion_type === 'discount_pct') dto.discount_pct = Number(form.discount_pct);
      if (form.promotion_type === 'fixed_amount_off')
        dto.fixed_amount_off = Number(form.fixed_amount_off);
      if (form.promotion_type === 'bonus') {
        dto.bonus_config = {
          bonus_product_id: form.bonus_product_id,
          bonus_quantity: Number(form.bonus_quantity),
        };
      }
      if (fromIso !== undefined) dto.effective_from = fromIso;
      if (toIso !== undefined) dto.effective_to = toIso;
      if (form.applicable_channels.length > 0) dto.applicable_channels = form.applicable_channels;
      createMutation.mutate(dto);
    }
  };

  const title = isEdit ? '編輯定價規則' : '新增定價規則';
  const bonusProductName =
    products.find((p) => p.id === form.bonus_product_id)?.name_i18n['zh-TW'] ?? '加贈商品';

  if (isEdit && pricingQuery.isLoading) return <div>載入中…</div>;
  if (isEdit && pricingQuery.error) {
    return <div className="error-banner">{(pricingQuery.error as Error).message}</div>;
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <div className="page-head">
        <h1 className="page-head__title">{title}（A-3）</h1>
        <div className="page-head__actions">
          <Link to="/pricing">
            <button>← 返回列表</button>
          </Link>
        </div>
      </div>

      <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
        一條規則 = 對「某商品的某票種」套用一種優惠。填完下方會即時試算顧客實付價。
        {isEdit && '（商品 / 票種 / 推廣類型建立後固定，僅可改其餘欄位）'}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14, marginTop: 16 }}>
        {/* ① 套用對象 */}
        <SectionTitle>① 套用對象</SectionTitle>
        <Row label="商品 *">
          <select
            value={form.product_id}
            onChange={(e) => onSelectProduct(e.target.value)}
            disabled={isEdit || productsQuery.isLoading}
            required
          >
            <option value="">— 選擇商品 —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name_i18n['zh-TW'] ?? p.sku}（{p.sku}）
              </option>
            ))}
          </select>
        </Row>
        <Row
          label="票種 *"
          hint={
            selectedProduct
              ? '只列該商品已定義之可售票種；要新增票種請到商品編輯的「票種票價」。牌價會自動帶入該票種原價。'
              : '此規則只套用到這個票種；先選商品才會限定可售票種。'
          }
        >
          <select
            value={form.ticket_type}
            onChange={(e) => onSelectTicketType(e.target.value as PricingTicketType)}
            disabled={isEdit}
            required
          >
            {sellableTicketTypes.map((t) => (
              <option key={t} value={t}>
                {TICKET_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Row>

        {/* ② 優惠內容 */}
        <SectionTitle>② 優惠內容</SectionTitle>
        <Row label="牌價（原價）*" hint="此票種的原價基準；選商品後自動帶入商品定價，可調整">
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.list_price}
            onChange={(e) => setForm((p) => ({ ...p, list_price: e.target.value }))}
            required
            style={{ width: 180 }}
          />
        </Row>
        <Row label="優惠方式 *">
          <select
            value={form.promotion_type}
            onChange={(e) =>
              setForm((p) => ({ ...p, promotion_type: e.target.value as PricingPromotionType }))
            }
            disabled={isEdit}
            required
          >
            {PRICING_PROMOTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {PROMOTION_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Row>

        {form.promotion_type === 'discount_pct' && (
          <Row
            label="折掉 %"
            hint="輸入「折掉的百分比」。例：20 = 折掉 20%（= 打 8 折）；10 = 打 9 折"
          >
            <input
              type="number"
              min={0.01}
              max={100}
              step="0.01"
              value={form.discount_pct}
              onChange={(e) => setForm((p) => ({ ...p, discount_pct: e.target.value }))}
              placeholder="20"
              required
              style={{ width: 140 }}
            />
            <span style={{ marginLeft: 6, color: '#6b7280' }}>%</span>
          </Row>
        )}

        {form.promotion_type === 'fixed_amount_off' && (
          <Row label="折抵金額" hint="從牌價直接減掉的金額（NT$）">
            <input
              type="number"
              min={0.01}
              step="0.01"
              value={form.fixed_amount_off}
              onChange={(e) => setForm((p) => ({ ...p, fixed_amount_off: e.target.value }))}
              required
              style={{ width: 180 }}
            />
          </Row>
        )}

        {form.promotion_type === 'bonus' && (
          <>
            <Row label="加贈商品" hint="顧客以原價購買，額外加贈此商品（不改變單價）">
              <select
                value={form.bonus_product_id}
                onChange={(e) => setForm((p) => ({ ...p, bonus_product_id: e.target.value }))}
                required
              >
                <option value="">— 選擇加贈商品 —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name_i18n['zh-TW'] ?? p.sku}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="加贈數量">
              <input
                type="number"
                min={1}
                step="1"
                value={form.bonus_quantity}
                onChange={(e) => setForm((p) => ({ ...p, bonus_quantity: e.target.value }))}
                required
                style={{ width: 120 }}
              />
            </Row>
          </>
        )}

        {form.promotion_type === 'package_bundle' && (
          <div
            style={{
              padding: 12,
              background: '#fef3c7',
              borderRadius: 4,
              fontSize: 13,
              color: '#92400e',
            }}
          >
            組合包（package_bundle）之 items / bundle_price 編輯介面尚未支援；請先以 API
            維護，或改用其他優惠方式。
          </div>
        )}

        {/* 即時試算預覽 */}
        {(form.promotion_type === 'discount_pct' || form.promotion_type === 'fixed_amount_off') && (
          <div style={previewCard}>
            {preview ? (
              <>
                <div style={{ fontSize: 13, color: '#6b7280' }}>顧客實付（此票種單價）</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#047857' }}>
                  {fmtNtd(preview.final)}
                </div>
                <div style={{ fontSize: 13, color: '#374151' }}>
                  原價{' '}
                  <span style={{ textDecoration: 'line-through' }}>{fmtNtd(preview.list)}</span> ·
                  省 {fmtNtd(preview.save)} · 約 {preview.zhe.toFixed(1)} 折
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                填入牌價與優惠後，這裡會即時顯示顧客實付價
              </div>
            )}
          </div>
        )}
        {form.promotion_type === 'bonus' && (
          <div style={previewCard}>
            <div style={{ fontSize: 13, color: '#374151' }}>
              顧客以原價購買，額外加贈 <strong>{form.bonus_quantity || '?'}</strong> 份「
              {bonusProductName}」（單價不變）
            </div>
          </div>
        )}

        {/* ③ 適用範圍與期間 */}
        <SectionTitle>③ 適用範圍與期間</SectionTitle>
        <Row label="適用通路" hint="不勾選 = 不限通路（含 Kiosk）；勾選後僅限選定通路套用">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {PRICING_APPLICABLE_CHANNELS.map((c) => (
              <label
                key={c}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={form.applicable_channels.includes(c)}
                  onChange={() => toggleChannel(c)}
                />
                {CHANNEL_LABEL[c]}
              </label>
            ))}
          </div>
        </Row>
        <Row label="生效期間" hint="留空 = 立即生效、永久有效">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="datetime-local"
              value={form.effective_from}
              onChange={(e) => setForm((p) => ({ ...p, effective_from: e.target.value }))}
            />
            <span style={{ color: '#6b7280' }}>～</span>
            <input
              type="datetime-local"
              value={form.effective_to}
              onChange={(e) => setForm((p) => ({ ...p, effective_to: e.target.value }))}
            />
          </div>
        </Row>
        <Row label="狀態">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
            <span style={{ fontSize: 13 }}>立即啟用此規則（未啟用則不會套用到售票）</span>
          </label>
        </Row>

        {formError && <div className="error-banner">{formError}</div>}
        {mutationError && <div className="error-banner">{(mutationError as Error).message}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? '送出中…' : isEdit ? '儲存變更' : '建立規則'}
          </button>
          <Link to="/pricing">
            <button type="button" disabled={submitting}>
              取消
            </button>
          </Link>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }): JSX.Element {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: '#111827',
        margin: '6px 0 0',
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: 6,
      }}
    >
      {children}
    </h2>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'start', gap: 10 }}
    >
      <label style={{ fontSize: 13, color: '#374151', paddingTop: 6 }}>{label}</label>
      <div>
        {children}
        {hint && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{hint}</div>}
      </div>
    </div>
  );
}

const previewCard: React.CSSProperties = {
  gridColumn: '1 / -1',
  marginLeft: 150,
  padding: 14,
  background: '#ecfdf5',
  border: '1px solid #a7f3d0',
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
